import React, { useState } from 'react';
import { gql } from '@soundxyz/gql-string';
import { FEATURES, PLAYLIST_INFO_ACTIONS } from '@soundxyz/utils/constants/logging';
import { LikePlaylistButton } from '@components/Likes/Button';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { PlayButton } from '@components/main-components/play-button/PlayButton';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment } from '@graphql/reactQuery';
import { PreviewHeaderFragmentDoc } from '@graphql/reactQuery';
import { useLogInfo } from '@hooks/useLogInfo';
import { usePlaylistPlayingState } from '@hooks/usePlaylistPlayingState';
import { getSingularOrPlural } from '@utils/helpers';
import { webappUriPath } from '@utils/url';
import { ShelfCreator } from '../ShelfCreator';
import { CoverImages, getPlaylistBackgroundImageUrl } from '../basics/CoverImages';
import { PreviewDropdown } from '../basics/Dropdown';
import { MintsDrivenTrigger } from '../basics/MintsDrivenTrigger';
import { NewBadge } from './NewBadge';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment PreviewHeader on Shelf {
    id
    name
    type
    numReferralPurchases
    releaseCount
    user {
      ...ShelfCreator
    }
    ...PlaylistCoverImages
    ...PlaylistPreviewDropdownInfo
  }
`);

const BULLET = '\u00A0•\u00A0';

export const PreviewHeader = ({
  playlistFrag,
  newPlaylist,
  trackIds,
  isOwner = false,
  disablePlay = false,
  playlistUrl,
}: {
  playlistFrag: FragmentType<PreviewHeaderFragmentDoc>;
  newPlaylist: boolean;
  trackIds: string[];
  isOwner?: boolean;
  disablePlay?: boolean;
  playlistUrl: string;
}) => {
  const logInfo = useLogInfo();
  const playlist = getFragment(PreviewHeaderFragmentDoc, playlistFrag);
  const { id, name, type, releaseCount, numReferralPurchases: mintsDriven } = playlist;
  const isUserCreated = type === 'DEFAULT';
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  const { isPlaying, onPlayButtonClick, loading } = usePlaylistPlayingState({
    playlistId: id,
  });

  const onClick = () => {
    logInfo({
      feature: FEATURES.PLAYLISTS,
      isArtistOnlyPage: false,
      message: 'Playlist preview row play button clicked',
      action: PLAYLIST_INFO_ACTIONS.PLAYLIST_PREVIEW_PLAY_CLICK,
      data: {
        playlistId: id,
      },
    });
    onPlayButtonClick();
  };

  return (
    <div className="relative h-[72px] w-full p-4 md:h-[138px] [&_.playlistPlayButton]:[@media_(hover:_hover)_and_(pointer:_fine)]:invisible">
      <div
        className="absolute left-0 top-0 h-full w-full rounded-t-md bg-cover bg-center"
        style={{ background: `url(${getPlaylistBackgroundImageUrl(playlist)})` }}
      >
        <div className="bg-playlist-preview-bg h-full w-full rounded-t-md bg-neutral900/50 backdrop-blur-[20px]" />
      </div>
      <div className="relative z-base flex h-full w-full items-center overflow-hidden text-white">
        <div className="flex flex-shrink-0 md:hidden">
          <PlayButton
            color="white"
            size="s"
            onClick={onClick}
            playing={isPlaying}
            disabled={disablePlay}
            elevated
            className="rounded-lg"
            loading={loading}
          />
        </div>
        <div className="relative hidden h-[106px] w-[106px] flex-shrink-0 items-center justify-center overflow-hidden rounded-[6px] md:flex">
          <CoverImages playlist={playlist} maxSize={106} priority={false} />
          <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center">
            <PlayButton
              color="white"
              size="s"
              onClick={onPlayButtonClick}
              playing={isPlaying}
              disabled={disablePlay}
              className="playlistPlayButton group-hover/preview:visible"
              elevated
            />
          </div>
        </div>
        <div className="ml-4 mr-2.5 h-10 w-full overflow-hidden text-white md:ml-6 md:h-[unset] lt:mr-1">
          <AccessibleLink
            className="flex items-center gap-2"
            pathnameUri={webappUriPath(playlistUrl)}
          >
            <div className="max-w-full overflow-hidden text-ellipsis whitespace-nowrap font-title text-title-m font-medium text-white">
              {name}
            </div>
            {newPlaylist && <NewBadge forHeader />}
          </AccessibleLink>
          <div className="mt-[2px] flex text-base-xs text-base300 md:mt-2">
            <ShelfCreator
              className="transition-[filter_0.3s_ease] hover:brightness-130"
              data={playlist.user}
              showAvatar={false}
            />
            {releaseCount > 0 ? (
              <>
                <div className="hidden md:inline">
                  {BULLET}
                  {releaseCount} {getSingularOrPlural({ count: releaseCount, word: 'sound' })}
                </div>
                {mintsDriven ? (
                  <>
                    {BULLET}
                    <MintsDrivenTrigger
                      className="transition-[filter] duration-300 hover:brightness-130"
                      playlistId={id}
                      mintsDriven={mintsDriven}
                      infoAction={PLAYLIST_INFO_ACTIONS.PLAYLIST_PREVIEW_MINTS_DRIVEN_CLICK}
                    />
                  </>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
        <div className="flex flex-shrink-0 gap-2 pr-1 md:mt-auto">
          {isUserCreated && !isOwner && (
            <LikePlaylistButton
              playlistId={id}
              disabled={false}
              variant="inverted"
              source="Playlist Preview"
            />
          )}
          <PreviewDropdown
            isDropdownOpen={isDropdownOpen}
            setIsDropdownOpen={setIsDropdownOpen}
            playlist={playlist}
            trackIds={trackIds}
            playlistLink={playlistUrl}
            isOwner={isOwner}
            playlistType={type}
            variant="inverted"
          />
        </div>
      </div>
    </div>
  );
};
